package br.com.vvrefeicoes.portal.feature.operacional

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.FlowRowScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.theme.AppPalette
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedCategory
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedCorporate
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedUnitReport
import br.com.vvrefeicoes.portal.domain.model.ConsolidatedWeek
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalFilters
import br.com.vvrefeicoes.portal.feature.shell.LocalGlobalPeriod
import com.composables.icons.lucide.BadgePercent
import com.composables.icons.lucide.Banknote
import com.composables.icons.lucide.ChevronDown
import com.composables.icons.lucide.ChevronUp
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.TrendingUp
import io.github.koalaplot.core.bar.DefaultBarPlotGroupedPointEntry
import io.github.koalaplot.core.bar.DefaultBarPosition
import io.github.koalaplot.core.bar.GroupedHorizontalBarPlot
import io.github.koalaplot.core.xygraph.CategoryAxisModel
import io.github.koalaplot.core.xygraph.XYGraph
import io.github.koalaplot.core.xygraph.rememberAxisContent
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.operational_corporate_admin_others
import portalvv.shared.generated.resources.operational_corporate_charges
import portalvv.shared.generated.resources.operational_corporate_payroll
import portalvv.shared.generated.resources.operational_corporate_taxes
import portalvv.shared.generated.resources.operational_corporate_title
import portalvv.shared.generated.resources.operational_detailed_categories_title
import portalvv.shared.generated.resources.operational_detailed_col_category
import portalvv.shared.generated.resources.operational_detailed_col_difference
import portalvv.shared.generated.resources.operational_detailed_col_planned
import portalvv.shared.generated.resources.operational_detailed_col_real
import portalvv.shared.generated.resources.operational_detailed_col_week
import portalvv.shared.generated.resources.operational_detailed_diff_chart_title
import portalvv.shared.generated.resources.operational_detailed_difference
import portalvv.shared.generated.resources.operational_detailed_empty
import portalvv.shared.generated.resources.operational_detailed_execution
import portalvv.shared.generated.resources.operational_detailed_total
import portalvv.shared.generated.resources.operational_detailed_total_planned
import portalvv.shared.generated.resources.operational_detailed_total_real
import portalvv.shared.generated.resources.operational_detailed_weekly_title
import kotlin.math.abs
import kotlin.math.round
import kotlin.math.roundToLong

private const val W_LABEL = 2.2f
private const val W_VALUE = 1.1f

private val KPI_HEIGHT = 96.dp

private val REAL_COLOR = AppPalette.Primary500
private val PLANNED_COLOR = AppPalette.Warning500
private const val REAL_SERIES_INDEX = 0

@Composable
fun ConsolidatedDetailedContent() {
    val controller = rememberConsolidatedDetailedController()
    val filters = LocalGlobalFilters.current
    val period = LocalGlobalPeriod.current

    LaunchedEffect(controller) {
        controller.init()
    }

    LaunchedEffect(period.selection) {
        controller.applyPeriod(period.selection)
    }

    LaunchedEffect(filters.unit, filters.manager, filters.director, filters.units) {
        controller.applyFilter(filters.selectedUnit, filters.selectedManager, filters.selectedDirector, filters.units)
    }

    when {
        controller.loading ->
            Box(
                modifier = Modifier.fillMaxWidth().height(200.dp),
                contentAlignment = Alignment.Center,
            ) {
                CircularProgressIndicator()
            }
        controller.reports.isEmpty() ->
            EmptyState(message = stringResource(Res.string.operational_detailed_empty))
        else -> {
            DetailedKpis(
                totalRealCents = controller.totalRealCents,
                totalPlannedCents = controller.totalPlannedCents,
                differenceCents = controller.differenceCents,
                executionPercent = controller.executionPercent,
            )
            DifferencesChartCard(controller.categoryDifferences)
            UnitsGrid(controller.reports)
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun DetailedKpis(
    totalRealCents: Long,
    totalPlannedCents: Long,
    differenceCents: Long,
    executionPercent: Double,
) {
    FlowRow(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
        maxItemsInEachRow = 4,
    ) {
        KpiTile(
            icon = Lucide.Banknote,
            iconColor = AppPalette.Warning500,
            iconContainerColor = AppPalette.Warning100,
            label = stringResource(Res.string.operational_detailed_total_planned),
            value = money(totalPlannedCents),
        )
        KpiTile(
            icon = Lucide.Banknote,
            iconColor = AppPalette.Primary500,
            iconContainerColor = AppPalette.Primary100,
            label = stringResource(Res.string.operational_detailed_total_real),
            value = money(totalRealCents),
        )
        KpiTile(
            icon = Lucide.TrendingUp,
            iconColor = differenceColor(differenceCents),
            iconContainerColor = differenceContainer(differenceCents),
            label = stringResource(Res.string.operational_detailed_difference),
            value = money(differenceCents),
            valueColor = differenceColor(differenceCents),
        )
        KpiTile(
            icon = Lucide.BadgePercent,
            iconColor = AppPalette.Info500,
            iconContainerColor = AppPalette.Info100,
            label = stringResource(Res.string.operational_detailed_execution),
            value = percentText(executionPercent),
        )
    }
}

@Composable
private fun FlowRowScope.KpiTile(
    icon: ImageVector,
    iconColor: Color,
    iconContainerColor: Color,
    label: String,
    value: String,
    valueColor: Color = MaterialTheme.colorScheme.onSurface,
) {
    Surface(
        modifier = Modifier.weight(1f).height(KPI_HEIGHT),
        shape = AppShapes.medium,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Surface(
                shape = AppShapes.small,
                color = iconContainerColor,
                modifier = Modifier.size(32.dp),
            ) {
                Box(contentAlignment = Alignment.Center) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = iconColor,
                        modifier = Modifier.size(16.dp),
                    )
                }
            }
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text(
                    text = label,
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = value,
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.SemiBold,
                    color = valueColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
        }
    }
}

@Composable
private fun DifferencesChartCard(differences: List<CategoryDifference>) {
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space16),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = stringResource(Res.string.operational_detailed_diff_chart_title),
                style = MaterialTheme.typography.titleMedium,
            )
            SeriesLegend(
                entries =
                    listOf(
                        LegendEntry(REAL_COLOR, stringResource(Res.string.operational_detailed_col_real)),
                        LegendEntry(PLANNED_COLOR, stringResource(Res.string.operational_detailed_col_planned)),
                    ),
            )
            if (differences.isEmpty()) {
                Text(
                    text = "—",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            } else {
                val ordered = differences.reversed()
                val names = ordered.map { stringResource(it.title) }
                val realValues = ordered.map { it.realCents.toFloat() / 100f }
                val plannedValues = ordered.map { it.plannedCents.toFloat() / 100f }
                val maxValue = (realValues + plannedValues).maxOrNull()?.coerceAtLeast(1f) ?: 1f
                val data =
                    names.mapIndexed { index, name ->
                        DefaultBarPlotGroupedPointEntry(
                            i = name,
                            d =
                                listOf(
                                    DefaultBarPosition(0f, realValues[index]),
                                    DefaultBarPosition(0f, plannedValues[index]),
                                ),
                        )
                    }
                XYGraph(
                    gridStyle = br.com.vvrefeicoes.portal.core.ui.chart.NoGridStyle,
                    xAxisModel = rememberValueAxis(maxValue, headroom = 1.2f),
                    yAxisModel = CategoryAxisModel(names),
                    xAxisContent = rememberAxisContent(labels = { value -> Text(money((value * 100).roundToLong()), style = MaterialTheme.typography.labelSmall) }),
                    yAxisContent = rememberAxisContent(labels = { value -> Text(value, style = MaterialTheme.typography.labelSmall) }),
                    modifier = Modifier.fillMaxWidth().height((names.size * 56 + 48).dp),
                ) {
                    val realLabel = stringResource(Res.string.operational_detailed_col_real)
                    val plannedLabel = stringResource(Res.string.operational_detailed_col_planned)
                    val differenceLabel = stringResource(Res.string.operational_detailed_col_difference)
                    GroupedHorizontalBarPlot(
                        data = data,
                        maxBarGroupWidth = 0.9f,
                        bar = { dataIndex, seriesIndex, value ->
                            val category = ordered.getOrNull(dataIndex)
                            val tooltip =
                                buildString {
                                    append(value.i)
                                    append('\n')
                                    append(realLabel)
                                    append(": ")
                                    append(money(category?.realCents ?: 0L))
                                    append('\n')
                                    append(plannedLabel)
                                    append(": ")
                                    append(money(category?.plannedCents ?: 0L))
                                    append('\n')
                                    append(differenceLabel)
                                    append(": ")
                                    append(money(category?.differenceCents ?: 0L))
                                }
                            HoverTooltip(text = tooltip) {
                                ValueBar(
                                    color = if (seriesIndex == REAL_SERIES_INDEX) REAL_COLOR else PLANNED_COLOR,
                                    label = compactMoney(((value.d.getOrNull(seriesIndex)?.end ?: 0f) * 100).roundToLong()),
                                    horizontal = true,
                                )
                            }
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun UnitsGrid(reports: List<ConsolidatedUnitReport>) {
    BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
        val columns =
            when {
                maxWidth >= 1440.dp -> 3
                maxWidth >= 980.dp -> 2
                else -> 1
            }
        Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space16)) {
            reports.chunked(columns).forEach { rowReports ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                ) {
                    rowReports.forEach { report ->
                        UnitReportCard(report = report, modifier = Modifier.weight(1f))
                    }
                    repeat(columns - rowReports.size) {
                        Spacer(Modifier.weight(1f))
                    }
                }
            }
        }
    }
}

@Composable
private fun UnitReportCard(
    report: ConsolidatedUnitReport,
    modifier: Modifier,
) {
    val difference = report.realTotalCents - report.plannedTotalCents
    var expanded by remember { mutableStateOf(emptySet<String>()) }
    Surface(
        modifier = modifier,
        shape = AppShapes.large,
        color = MaterialTheme.colorScheme.surface,
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().padding(AppSpacing.space12),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space12),
        ) {
            Text(
                text = report.unitName,
                style = MaterialTheme.typography.titleSmall,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8),
            ) {
                SummaryValue(stringResource(Res.string.operational_detailed_col_real), money(report.realTotalCents), Modifier.weight(1f))
                SummaryValue(stringResource(Res.string.operational_detailed_col_planned), money(report.plannedTotalCents), Modifier.weight(1f))
                SummaryValue(
                    stringResource(Res.string.operational_detailed_col_difference),
                    money(difference),
                    Modifier.weight(1f),
                    differenceColor(difference),
                )
            }
            WeeklyTable(weeks = report.weeks)
            CategoryTable(
                categories = report.categories,
                expanded = expanded,
                onToggle = { categoryId ->
                    expanded = if (categoryId in expanded) expanded - categoryId else expanded + categoryId
                },
            )
            CorporateSection(corporate = report.corporate)
        }
    }
}

@Composable
private fun SummaryValue(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    valueColor: Color = MaterialTheme.colorScheme.onSurface,
) {
    Column(
        modifier = modifier,
        horizontalAlignment = Alignment.End,
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space4),
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
        Text(
            text = value,
            style = MaterialTheme.typography.bodySmall,
            fontWeight = FontWeight.SemiBold,
            color = valueColor,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis,
        )
    }
}

@Composable
private fun WeeklyTable(weeks: List<ConsolidatedWeek>) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        Text(
            text = stringResource(Res.string.operational_detailed_weekly_title),
            style = MaterialTheme.typography.labelLarge,
        )
        if (weeks.isEmpty()) {
            Text(
                text = "—",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            TableHeader(firstColumn = stringResource(Res.string.operational_detailed_col_week))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            weeks.forEachIndexed { index, week ->
                Row(
                    modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space4),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Cell("${index + 1} · ${week.label}", W_LABEL, TextAlign.Start)
                    DifferenceCells(week.realCents, week.plannedCents)
                }
                if (index < weeks.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
            }
            TotalRow(real = weeks.map { it.realCents }, planned = weeks.map { it.plannedCents })
        }
    }
}

@Composable
private fun CorporateSection(corporate: ConsolidatedCorporate) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        Text(
            text = stringResource(Res.string.operational_corporate_title),
            style = MaterialTheme.typography.labelLarge,
        )
        TableHeader(firstColumn = stringResource(Res.string.operational_detailed_col_category))
        HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
        CorporateLine(stringResource(Res.string.operational_corporate_payroll), corporate.payrollRealCents, corporate.payrollPlannedCents)
        CorporateLine(stringResource(Res.string.operational_corporate_charges), corporate.chargesRealCents, corporate.chargesPlannedCents)
        CorporateLine(stringResource(Res.string.operational_corporate_admin_others), corporate.adminOthersRealCents, corporate.adminOthersPlannedCents)
        CorporateLine(stringResource(Res.string.operational_corporate_taxes), corporate.taxesRealCents, corporate.taxesPlannedCents)
        TotalRow(
            real = listOf(corporate.payrollRealCents, corporate.chargesRealCents, corporate.adminOthersRealCents, corporate.taxesRealCents),
            planned = listOf(corporate.payrollPlannedCents, corporate.chargesPlannedCents, corporate.adminOthersPlannedCents, corporate.taxesPlannedCents),
        )
    }
}

@Composable
private fun CorporateLine(
    label: String,
    real: Long,
    planned: Long,
) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space4),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Cell(label, W_LABEL, TextAlign.Start)
        DifferenceCells(real, planned)
    }
}

@Composable
private fun CategoryTable(
    categories: List<ConsolidatedCategory>,
    expanded: Set<String>,
    onToggle: (String) -> Unit,
) {
    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        Text(
            text = stringResource(Res.string.operational_detailed_categories_title),
            style = MaterialTheme.typography.labelLarge,
        )
        if (categories.isEmpty()) {
            Text(
                text = "—",
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        } else {
            TableHeader(firstColumn = stringResource(Res.string.operational_detailed_col_category))
            HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
            categories.forEachIndexed { index, category ->
                val isExpanded = category.categoryId in expanded
                Row(
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .clickable { onToggle(category.categoryId) }
                            .padding(vertical = AppSpacing.space4),
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    Row(
                        modifier = Modifier.weight(W_LABEL),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
                    ) {
                        Icon(
                            imageVector = if (isExpanded) Lucide.ChevronUp else Lucide.ChevronDown,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.size(16.dp),
                        )
                        Text(
                            text = stringResource(category.title),
                            style = MaterialTheme.typography.bodySmall,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                            modifier = Modifier.weight(1f, fill = false),
                        )
                    }
                    DifferenceCells(category.realCents, category.plannedCents)
                }
                if (isExpanded) {
                    category.suppliers.forEach { supplier ->
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space4),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            Row(
                                modifier = Modifier.weight(W_LABEL),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(AppSpacing.space4),
                            ) {
                                Spacer(Modifier.width(20.dp))
                                Text(
                                    text = supplier.name,
                                    style = MaterialTheme.typography.labelSmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                                    maxLines = 1,
                                    overflow = TextOverflow.Ellipsis,
                                    modifier = Modifier.weight(1f, fill = false),
                                )
                            }
                            DifferenceCells(supplier.realCents, supplier.plannedCents)
                        }
                    }
                }
                if (index < categories.lastIndex) {
                    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
                }
            }
            TotalRow(real = categories.map { it.realCents }, planned = categories.map { it.plannedCents })
        }
    }
}

@Composable
private fun TableHeader(firstColumn: String) {
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space4),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        HeaderCell(firstColumn, W_LABEL, TextAlign.Start)
        HeaderCell(stringResource(Res.string.operational_detailed_col_real), W_VALUE, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_detailed_col_planned), W_VALUE, TextAlign.End)
        HeaderCell(stringResource(Res.string.operational_detailed_col_difference), W_VALUE, TextAlign.End)
    }
}

@Composable
private fun TotalRow(
    real: List<Long>,
    planned: List<Long>,
) {
    val totalReal = real.sum()
    val totalPlanned = planned.sum()
    HorizontalDivider(color = MaterialTheme.colorScheme.outlineVariant)
    Row(
        modifier = Modifier.fillMaxWidth().padding(vertical = AppSpacing.space8),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Cell(stringResource(Res.string.operational_detailed_total), W_LABEL, TextAlign.Start, bold = true)
        ValueCell(money(totalReal), W_VALUE, bold = true)
        ValueCell(money(totalPlanned), W_VALUE, bold = true)
        ValueCell(money(totalReal - totalPlanned), W_VALUE, color = differenceColor(totalReal - totalPlanned), bold = true)
    }
}

@Composable
private fun RowScope.DifferenceCells(
    realCents: Long,
    plannedCents: Long,
) {
    val difference = realCents - plannedCents
    ValueCell(money(realCents), W_VALUE)
    ValueCell(money(plannedCents), W_VALUE)
    ValueCell(money(difference), W_VALUE, color = differenceColor(difference))
}

@Composable
private fun RowScope.HeaderCell(
    text: String,
    weight: Float,
    align: TextAlign,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelSmall,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = align,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space4),
    )
}

@Composable
private fun RowScope.Cell(
    text: String,
    weight: Float,
    align: TextAlign,
    bold: Boolean = false,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        fontWeight = if (bold) FontWeight.SemiBold else FontWeight.Normal,
        color = MaterialTheme.colorScheme.onSurface,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = align,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space4),
    )
}

@Composable
private fun RowScope.ValueCell(
    text: String,
    weight: Float,
    color: Color = MaterialTheme.colorScheme.onSurface,
    bold: Boolean = false,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodySmall,
        fontWeight = if (bold) FontWeight.SemiBold else FontWeight.Normal,
        color = color,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis,
        textAlign = TextAlign.End,
        modifier = Modifier.weight(weight).padding(horizontal = AppSpacing.space4),
    )
}

private fun differenceColor(cents: Long): Color = if (cents > 0) AppPalette.Error500 else AppPalette.Success500

private fun differenceContainer(cents: Long): Color = if (cents > 0) AppPalette.Error100 else AppPalette.Success100

private fun money(cents: Long): String = "R$ ${MoneyFormatter.formatCents(cents)}"

private fun percentText(value: Double): String {
    val scaled = round(value * 10).toLong()
    return "${scaled / 10},${abs(scaled % 10)}%"
}
